import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Sidebar } from 'components/layout/Sidebar'
import { Header } from 'components/layout/Header'

const AiChromeContext = createContext<{ setHistoryOpen: (open: boolean) => void }>({
  setHistoryOpen: () => undefined,
})

export function useAiChrome() {
  return useContext(AiChromeContext)
}

const AI_AGENT_PATH = '/copilot'

function isAiAgentPath(pathname: string) {
  return pathname === AI_AGENT_PATH
    || pathname.startsWith(`${AI_AGENT_PATH}/`)
    || /^\/organizations\/\d+\/copilot\/users\/\d+\/?$/.test(pathname)
}

export function Layout() {
  const location = useLocation()
  const canHideSidebar = isAiAgentPath(location.pathname)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [isAiSidebarHidden, setIsAiSidebarHidden] = useState(true)
  const [isNavCollapsed, setIsNavCollapsed] = useState(false)
  const [trackedPath, setTrackedPath] = useState(location.pathname)
  const [isRightHistoryOpen, setIsRightHistoryOpen] = useState(false)
  const isDesktopSidebarHidden = canHideSidebar ? isAiSidebarHidden : isNavCollapsed
  const hideHeaderForHistory = canHideSidebar && isRightHistoryOpen
  const aiChromeValue = useMemo(() => ({ setHistoryOpen: setIsRightHistoryOpen }), [])

  if (location.pathname !== trackedPath) {
    setTrackedPath(location.pathname)
    if (canHideSidebar) {
      setIsAiSidebarHidden(true)
      setIsMobileSidebarOpen(false)
    }
  }

  useEffect(() => {
    if (!canHideSidebar) setIsRightHistoryOpen(false)
  }, [canHideSidebar])

  const closeMobileSidebar = () => setIsMobileSidebarOpen(false)
  const collapseDesktopSidebar = () => {
    if (canHideSidebar) setIsAiSidebarHidden(true)
    else setIsNavCollapsed(true)
  }
  const expandDesktopSidebar = () => {
    if (canHideSidebar) setIsAiSidebarHidden(false)
    else setIsNavCollapsed(false)
  }

  const handleToggleSidebar = () => setIsMobileSidebarOpen(true)

  return (
    <div className="flex h-screen overflow-hidden bg-muted">
      <Sidebar
        isOpen={isMobileSidebarOpen}
        onClose={closeMobileSidebar}
        canCollapseOnDesktop={canHideSidebar}
        showCollapseToggle
        isDesktopCollapsed={isDesktopSidebarHidden}
        onCollapseDesktop={collapseDesktopSidebar}
        onExpandDesktop={expandDesktopSidebar}
      />
      <div className="flex-1 flex flex-col overflow-hidden w-full">
        <div className="flex-1 flex flex-col overflow-hidden bg-background">
          <div className={`flex-shrink-0 ${hideHeaderForHistory ? 'lg:hidden' : ''}`}>
            <Header onToggleSidebar={handleToggleSidebar} />
          </div>
          <main className={`flex-1 ${canHideSidebar ? 'overflow-hidden p-0' : 'overflow-y-auto p-4 sm:p-6 lg:p-8'}`}>
            <AiChromeContext.Provider value={aiChromeValue}>
              <Outlet />
            </AiChromeContext.Provider>
          </main>
        </div>
      </div>
    </div>
  )
}
